import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import CollapsibleSection from '@/components/superuser/CollapsibleSection';

interface Props {
  variables: Record<string, string> | undefined;
}

export default function DynamicVariablesPanel({ variables }: Props) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const json = variables ? JSON.stringify(variables, null, 2) : '';

  const onCopy = async () => {
    if (!variables) return;
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* noop */
    }
  };

  const copyButton = variables ? (
    <Button variant="outline" size="sm" onClick={onCopy}>
      {copied ? (
        <>
          <Check className="mr-1 size-4" />
          {t('superuser.debug.detail.briefing.copied', 'Copiado')}
        </>
      ) : (
        <>
          <Copy className="mr-1 size-4" />
          {t('superuser.debug.detail.briefing.copy', 'Copiar')}
        </>
      )}
    </Button>
  ) : undefined;

  return (
    <CollapsibleSection
      title={t('superuser.debug.detail.dynvars.title', 'Dynamic variables (payload ElevenLabs)')}
      headerExtra={copyButton}
    >
      {variables ? (
        <pre className="max-h-[480px] overflow-auto rounded-md border bg-muted/30 p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap break-words">
          {json}
        </pre>
      ) : (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <p>
            {t(
              'superuser.debug.detail.dynvars.legacyWarning',
              'Esta sesión es anterior a la persistencia de dynamic variables. No hay snapshot disponible.',
            )}
          </p>
        </div>
      )}
    </CollapsibleSection>
  );
}
